/**
 * Product QR Code Generator
 *
 * Generates printable QR codes for store products.
 * Useful for creating shelf labels or product tags.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStoreAuth } from '../../contexts/StoreAuthContext';
import StoreHeader from '../../components/Layout/StoreHeader';

interface ProductQR {
  id: number;
  name: string;
  sku: string;
  barcode: string;
  category: string | null;
  price: number;
  size_lbs: number | null;
  qr_image: string;
}

interface Category {
  id: number;
  name: string;
}

const API_BASE = '/api';

const ProductQRCodes: React.FC = () => {
  const { isAuthenticated } = useStoreAuth();
  const navigate = useNavigate();

  const [products, setProducts] = useState<ProductQR[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedProducts, setSelectedProducts] = useState<Set<number>>(new Set());
  const [labelSize, setLabelSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [showPrice, setShowPrice] = useState(true);
  const [printMode, setPrintMode] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/store/login');
      return;
    }
    loadCategories();
    loadProducts();
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    loadProducts();
  }, [selectedCategory]);

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/pos/categories/`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to load categories');
      const data = await response.json();
      setCategories(data.categories);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      let url = `${API_BASE}/qr/products/`;
      if (selectedCategory) {
        url += `?category=${selectedCategory}`;
      }
      const response = await fetch(url, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to load products');
      const data = await response.json();
      setProducts(data.products);
    } catch (err) {
      console.error('Failed to load products:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleProduct = (productId: number) => {
    setSelectedProducts(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(products.map(p => p.id)));
    }
  };

  const handlePrint = () => {
    setPrintMode(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintMode(false), 500);
    }, 100);
  };

  const selectedProductsList = products.filter(p => selectedProducts.has(p.id));

  const labelSizes = {
    small: { width: '120px', qrSize: '80px', fontSize: '0.65rem' },
    medium: { width: '160px', qrSize: '110px', fontSize: '0.75rem' },
    large: { width: '220px', qrSize: '150px', fontSize: '0.85rem' },
  };

  const currentSize = labelSizes[labelSize];

  if (!isAuthenticated) return null;

  return (
    <div style={printMode ? styles.printPage : styles.page}>
      {!printMode && <StoreHeader />}

      <main style={printMode ? styles.printMain : styles.main}>
        {!printMode && (
          <>
            <div style={styles.header}>
              <h1 style={styles.title}>Product QR Codes</h1>
              <button
                style={styles.backBtn}
                onClick={() => navigate('/store/admin')}
              >
                Back to Admin
              </button>
            </div>

            <div style={styles.controls}>
              <div style={styles.controlGroup}>
                <label style={styles.label}>Category:</label>
                <select
                  value={selectedCategory}
                  onChange={e => setSelectedCategory(e.target.value)}
                  style={styles.select}
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>Size:</label>
                <select
                  value={labelSize}
                  onChange={e => setLabelSize(e.target.value as 'small' | 'medium' | 'large')}
                  style={styles.select}
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={showPrice}
                    onChange={e => setShowPrice(e.target.checked)}
                    style={styles.checkboxInput}
                  />
                  Show Price
                </label>
              </div>

              <button style={styles.selectAllBtn} onClick={toggleAll}>
                {selectedProducts.size === products.length ? 'Deselect All' : 'Select All'}
              </button>

              <button
                style={styles.printBtn}
                onClick={handlePrint}
                disabled={selectedProducts.size === 0}
              >
                Print ({selectedProducts.size})
              </button>
            </div>

            {loading ? (
              <p style={styles.loading}>Loading products...</p>
            ) : (
              <div style={styles.productList}>
                {products.map(product => (
                  <div
                    key={product.id}
                    style={{
                      ...styles.productItem,
                      backgroundColor: selectedProducts.has(product.id) ? '#e8f5e9' : '#fff',
                      borderColor: selectedProducts.has(product.id) ? '#1a4d1a' : '#ddd',
                    }}
                    onClick={() => toggleProduct(product.id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedProducts.has(product.id)}
                      onChange={() => toggleProduct(product.id)}
                      style={styles.checkbox}
                    />
                    <div style={styles.productInfo}>
                      <strong>{product.name}</strong>
                      <span style={styles.productDetail}>
                        {product.sku} | ${product.price.toFixed(2)}
                        {product.size_lbs && ` | ${product.size_lbs}lb`}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Print Preview / Printable Labels */}
        <div style={printMode ? styles.labelGridPrint : styles.labelGrid}>
          {selectedProductsList.map(product => (
            <div
              key={product.id}
              style={{
                ...styles.labelCard,
                width: currentSize.width,
              }}
            >
              <img
                src={product.qr_image}
                alt={`QR for ${product.name}`}
                style={{
                  width: currentSize.qrSize,
                  height: currentSize.qrSize,
                }}
              />
              <div style={{ ...styles.labelText, fontSize: currentSize.fontSize }}>
                <div style={styles.productName}>{product.name}</div>
                <div style={styles.productSku}>{product.sku}</div>
                {showPrice && (
                  <div style={styles.price}>${product.price.toFixed(2)}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>

      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-area, .print-area * {
            visibility: visible;
          }
          .print-area {
            position: absolute;
            left: 0;
            top: 0;
          }
        }
      `}</style>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f0f0f0',
    display: 'flex',
    flexDirection: 'column',
  },
  printPage: {
    backgroundColor: '#fff',
  },
  main: {
    flex: 1,
    padding: '1.5rem',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
  },
  printMain: {
    padding: '0.5rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
    margin: 0,
  },
  backBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#666',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  controls: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: '1rem',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  controlGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  label: {
    fontWeight: '500',
    color: '#333',
    fontSize: '0.9rem',
  },
  select: {
    padding: '0.5rem',
    border: '2px solid #ddd',
    borderRadius: '6px',
    fontSize: '0.9rem',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    cursor: 'pointer',
    fontSize: '0.9rem',
    color: '#333',
  },
  checkboxInput: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  },
  selectAllBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#f5f5f5',
    border: '2px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  printBtn: {
    padding: '0.5rem 1.5rem',
    backgroundColor: '#1a4d1a',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '600',
    marginLeft: 'auto',
  },
  loading: {
    textAlign: 'center',
    color: '#666',
    padding: '2rem',
  },
  productList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '0.75rem',
    marginBottom: '2rem',
    maxHeight: '300px',
    overflowY: 'auto',
  },
  productItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem',
    backgroundColor: '#fff',
    border: '2px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },
  productInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
    flex: 1,
  },
  productDetail: {
    fontSize: '0.8rem',
    color: '#666',
  },
  labelGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    justifyContent: 'center',
    padding: '1rem',
    backgroundColor: '#fff',
    borderRadius: '12px',
    minHeight: '200px',
  },
  labelGridPrint: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.25rem',
    justifyContent: 'flex-start',
    pageBreakInside: 'avoid',
  },
  labelCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0.5rem',
    border: '1px solid #ddd',
    borderRadius: '6px',
    backgroundColor: '#fff',
    pageBreakInside: 'avoid',
  },
  labelText: {
    textAlign: 'center',
    marginTop: '0.25rem',
  },
  productName: {
    fontWeight: 'bold',
    color: '#333',
    lineHeight: 1.2,
  },
  productSku: {
    color: '#666',
    marginTop: '0.125rem',
    fontFamily: 'monospace',
  },
  price: {
    fontWeight: '600',
    color: '#1a4d1a',
    marginTop: '0.125rem',
  },
};

export default ProductQRCodes;
